/**
 * ConsentModal — modal LGPD de consentimento de gravação (PRD 4, Story 4.4.2).
 *
 * Mostrado ANTES de qualquer publish de mídia em chamada de vídeo. Não-dismissível
 * por overlay click ou ESC — partner precisa decidir explicitamente (aceitar ou
 * recusar). AlertDialog do Radix garante esse comportamento por default.
 *
 * Fluxo de aceitar:
 *   1. Click em "Aceitar e iniciar chamada".
 *   2. Mutation POST /portal/atendimentos/{id}/consent (`useRecordConsent`).
 *   3. Em sucesso, dispara `onAccept(response)`. Parent decide o que fazer
 *      (tipicamente pedir token LiveKit em seguida).
 *
 * Fluxo de recusar:
 *   - `refuseFallback="audio-only"`: dispara `onRefuse('audio-only')` direto.
 *     Sem perda — chamada segue só com áudio.
 *   - `refuseFallback="end"`: mostra confirmação inline ("Tem certeza? A chamada
 *     será encerrada."), e só dispara `onRefuse('end')` após segundo click.
 *     Confirmação obrigatória porque a ação é destrutiva (encerra a chamada).
 *
 * Error state: se a mutation falhar (rede/server 5xx), mostra `<Alert variant=
 * "destructive">` com mensagem + botão "Tentar novamente". Botão recusar continua
 * funcionando — partner pode optar pelo fallback se não conseguir consentir.
 *
 * Acessibilidade: AlertDialog do Radix já injeta `role="alertdialog"`,
 * `aria-labelledby` no Title e `aria-describedby` no Description. Foco
 * inicial vai pro AlertDialogAction (aceitar) — comportamento default.
 *
 * Source of truth dos endpoints: V1/backend/app/api/v1/endpoints/portal_atendimentos.py
 */

import { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useRecordConsent } from "@/hooks/usePortalAtendimento";
import type { RecordConsentResponse } from "@/types/atendimento";


export type ConsentRefuseFallback = "audio-only" | "end";

export interface ConsentModalProps {
  /** Quando `false`, o modal não é renderizado no DOM. */
  open: boolean;
  /** UUID da sessão alvo do consent. */
  sessionId: string;
  /**
   * O que fazer quando o parceiro recusa o consent.
   * - `"audio-only"`: parent continua a chamada só com áudio (sem perda).
   * - `"end"`: parent encerra a chamada — exige confirmação extra do parceiro.
   */
  refuseFallback: ConsentRefuseFallback;
  /** Callback chamado após mutation bem-sucedida. Recebe o response do backend. */
  onAccept: (response: RecordConsentResponse) => void;
  /**
   * Callback chamado quando parceiro recusa. Recebe o fallback configurado para
   * que o parent saiba qual ação tomar (não há novo cálculo — é só passthrough).
   */
  onRefuse: (fallback: ConsentRefuseFallback) => void;
}


export function ConsentModal({
  open,
  sessionId,
  refuseFallback,
  onAccept,
  onRefuse,
}: ConsentModalProps) {
  const consentMutation = useRecordConsent(sessionId);
  // Estado local da confirmação de recusa quando fallback="end" — só usado
  // pra mostrar o segundo botão "Sim, encerrar". Não vaza pro parent.
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  const isLoading = consentMutation.isPending;
  const isError = consentMutation.isError;

  const handleAccept = () => {
    consentMutation.mutate(undefined, {
      onSuccess: (response) => {
        onAccept(response);
      },
    });
  };

  const handleRefuse = () => {
    if (refuseFallback === "audio-only") {
      onRefuse("audio-only");
      return;
    }
    // refuseFallback === "end" — duas etapas.
    if (!confirmingEnd) {
      setConfirmingEnd(true);
      return;
    }
    onRefuse("end");
  };

  const refuseLabel =
    refuseFallback === "audio-only"
      ? "Recusar (continuar só com áudio)"
      : confirmingEnd
        ? "Sim, encerrar chamada"
        : "Recusar";

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Consentimento de gravação (LGPD)</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 text-left">
            <span className="block">
              Esta chamada será <strong>gravada</strong> para fins de auditoria,
              treinamento e cumprimento de requisitos LGPD.
            </span>
            <span className="block">
              Ao aceitar, você consente que seu áudio e vídeo serão capturados,
              armazenados de forma segura e retidos pelo prazo definido pelo setor.
            </span>
            <span className="block">
              Você pode solicitar a exclusão dos dados a qualquer momento via{" "}
              <code>/portal/lgpd</code>.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isError && (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Não foi possível registrar o consentimento. Verifique sua conexão e
              tente novamente.
            </AlertDescription>
          </Alert>
        )}

        {refuseFallback === "end" && confirmingEnd && (
          <Alert variant="warning" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Tem certeza? A chamada será encerrada e o atendimento ficará sem
              vídeo gravado.
            </AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleRefuse}
            disabled={isLoading}
          >
            {refuseLabel}
          </Button>
          <Button type="button" onClick={handleAccept} disabled={isLoading} autoFocus>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Registrando consentimento...
              </>
            ) : isError ? (
              "Tentar novamente"
            ) : (
              "Aceitar e iniciar chamada"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
